/**
 * Wager entry (M3.5, FR-5.3/6.2 — one control, one action). A single numeric
 * input + submit button for a Daily Double or Final Jeopardy wager. The bounds
 * are server-computed (`WagerBounds`) and shown; the button is disabled until the
 * value is a whole number within `[min, max]` so an illegal wager can't be sent.
 * The server re-validates regardless (authority stays server-side).
 */

import { useState } from 'react';
import { isWagerInBounds, type WagerBounds } from '@jeopardy/shared';

export interface WagerInputProps {
  bounds: WagerBounds;
  /** Label for the wagering context (e.g. "Daily Double" / "Final Jeopardy"). */
  context: string;
  onSubmit: (amount: number) => void;
}

export function WagerInput({
  bounds,
  context,
  onSubmit,
}: WagerInputProps): React.JSX.Element {
  const [value, setValue] = useState('');
  const amount = Number(value);
  const valid = value.trim() !== '' && isWagerInBounds(amount, bounds);

  return (
    <form
      className="wager-input"
      data-testid="wager-input"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onSubmit(amount);
      }}
    >
      <label className="wager-input__label">
        {context} wager (${bounds.min}–${bounds.max})
      </label>
      <input
        type="number"
        inputMode="numeric"
        min={bounds.min}
        max={bounds.max}
        step={1}
        aria-label={`${context} wager`}
        data-testid="wager-amount"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoFocus
      />
      <button
        type="submit"
        className="btn btn--primary"
        data-testid="wager-submit"
        disabled={!valid}
      >
        Submit wager
      </button>
    </form>
  );
}
